import i18next from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { i18n as i18nType } from 'i18next';
import { writable } from 'svelte/store';
import type { I18nOverrides } from '$lib/utils/translationDictionary';

import { assembleSettingsTranslations } from './settings-translations';
import { resolveBundledLanguage } from './resolveLanguage'; // issue 109

let overrides: I18nOverrides = {};

type Resource = Record<string, unknown>;

const isPlainObject = (value: unknown): value is Resource =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const deepMerge = (base: Resource, override: Resource): Resource => {
	const merged: Resource = { ...base };
	for (const [key, value] of Object.entries(override)) {
		const current = merged[key];
		merged[key] = isPlainObject(current) && isPlainObject(value)
			? deepMerge(current, value)
			: value;
	}
	return merged;
};

// 三层，后者覆盖前者：locales/（上游原样）→ overrides/（我方，构建期）→ 管理员后台改的文案（上游，模块变量 overrides，见 loadResource）。
// locales/ 保持上游原样，自研文案放 overrides/，合并上游时零冲突。
// 只有 zh-CN / en-US 有 overrides，其余语种缺失的 key 由 fallbackLng 回落到 en-US。
export const loadBundledResource = async (requested: string): Promise<Record<string, string>> => {
	// issue 109：`en` / `zh` 等没有对应目录的代码先解析到实际存在的 locales 目录，避免整套翻译加载失败
	const codes = ((await import('./locales/languages.json')).default as { code: string }[]).map((l) => l.code);
	const language = resolveBundledLanguage(requested, codes);
	const base = (await import(`./locales/${language}/translation.json`)).default;
	try {
		const override = (await import(`./overrides/${language}/translation.json`)).default;
		return deepMerge(base, override) as Record<string, string>;
	} catch {
		return base;
	}
};

const loadResource = async (language: string) =>
	assembleSettingsTranslations(
		await loadBundledResource(language),
		await loadBundledResource('en-US'),
		overrides[language],
		overrides['en-US']
	);

export const updateI18n = async (value: I18nOverrides = {}) => {
	overrides = value;
	const resources = await Promise.all(
		Object.keys(i18next.store?.data ?? {}).map(async (language) => ({
			language,
			resource: await loadResource(language)
		}))
	);
	for (const { language, resource } of resources) {
		i18next.removeResourceBundle(language, 'translation');
		i18next.addResourceBundle(language, 'translation', resource);
	}
	i18n.set(i18next);
};

const createI18nStore = (i18n: i18nType) => {
	const i18nWritable = writable(i18n);

	i18n.on('initialized', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('loaded', () => {
		i18nWritable.set(i18n);
	});
	i18n.on('added', () => i18nWritable.set(i18n));
	i18n.on('languageChanged', (lang) => {
		i18nWritable.set(i18n);
		if (typeof document !== 'undefined') {
			document.documentElement.setAttribute('lang', lang);
		}
	});
	return i18nWritable;
};

const createIsLoadingStore = (i18n: i18nType) => {
	const isLoading = writable(false);

	// if loaded resources are empty || {}, set loading to true
	i18n.on('loaded', (resources) => {
		// console.log('loaded:', resources);
		isLoading.set(Object.keys(resources).length === 0);
	});

	// if resources failed loading, set loading to true
	i18n.on('failedLoading', () => {
		isLoading.set(true);
	});

	return isLoading;
};

export const initI18n = (defaultLocale?: string, value: I18nOverrides = {}) => {
	overrides = value;
	const detectionOrder = defaultLocale
		? ['querystring', 'localStorage']
		: ['querystring', 'localStorage', 'navigator'];
	const fallbackDefaultLocale = defaultLocale ? [defaultLocale] : ['en-US'];

	return i18next
		.use(resourcesToBackend(loadResource))
		.use(LanguageDetector)
		.init({
			debug: false,
			detection: {
				order: detectionOrder,
				caches: ['localStorage'],
				lookupQuerystring: 'lang',
				lookupLocalStorage: 'locale'
			},
			fallbackLng: {
				fr: ['fr-FR'],
				default: fallbackDefaultLocale
			},
			ns: 'translation',
			keySeparator: false,
			nsSeparator: false,
			returnEmptyString: false,
			interpolation: {
				escapeValue: false // not needed for svelte as it escapes by default
			}
		});
};

const i18n = createI18nStore(i18next);
const isLoadingStore = createIsLoadingStore(i18next);

export const getLanguages = async () => {
	const languages = (await import(`./locales/languages.json`)).default;
	return languages;
};
export const changeLanguage = (lang: string) => {
	document.documentElement.setAttribute('lang', lang);
	return i18next.changeLanguage(lang);
};

export default i18n;
export const isLoading = isLoadingStore;
